<template>
  <div class="m-details">
    <template v-if="eventId">
      <PrematchDetails
        v-if="isPrematch"
        ref="mainDetail"
        @change-tab="onChangeNav"
      />

      <LiveDetails
        v-else
        ref="mainDetail"
        @change-tab="onChangeNav"
      />

      <keep-alive>
        <MiniFooter
          v-if="miniFooterVisible"
          ref="miniFooter"
        />
      </keep-alive>
    </template>
    <Empty
      v-else
      text="Sorry, the match id is empty. Please select a valid match."
    />
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import useMatchInfo, { setParamSportId, resetMatchInfo } from '@ms/components/match/useMatchInfo.js'
import { useDetailStore } from '@ms/stores/matchDetail'
import { useMatchDetailStore } from 'wap/stores/matchDetail'
import { useWapBetslipStore } from 'wap/stores/betslip'
import eventBus from '@ms/utils/event-bus.js'
import Empty from 'wap/components/Empty'
import LiveDetails from './Live'
import PrematchDetails from './Prematch'
import MiniFooter from 'wap/components/Footer/MiniFooter.vue'
import { useDomThemeSwitcher } from 'composables/useDomThemeSwitcher.js'

export default {
  name: 'MatchDetail',
  components: {
    Empty,
    LiveDetails,
    PrematchDetails,
    MiniFooter
  },
  props: {
    eventId: {
      type: String,
      default: '',
      required: true
    },
    tab: {
      type: String,
      default: ''
    }
  },
  setup() {
    const { isLive, isPrematch } = useMatchInfo()
    const matchDetailStore = useMatchDetailStore()
    const { event, isCommentary } = storeToRefs(matchDetailStore)

    const { subMarkets, subEvent, updateEventId, resetEvent, resetLiveCommentary } = matchDetailStore

    const { selectionNum } = storeToRefs(useWapBetslipStore())

    return {
      isLive,
      isPrematch,
      event,
      isCommentary,
      subMarkets,
      subEvent,
      updateEventId,
      resetEvent,
      resetLiveCommentary,
      selectionNum
    }
  },
  data() {
    return {
      currentTab: '',
      miniFooterVisible: false,
      isVSoccer: false,
      hideBall: false
    }
  },
  computed: {
    isTabAssist() {
      return /assist/i.test(this.currentTab)
    },
    isTabStats() {
      return /stats/i.test(this.currentTab)
    },
    source() {
      return this.isLive ? 'LiveMatchDetail' : 'MatchDetail'
    },
    shouldHideBetslipBall() {
      return this.isTabAssist || this.isTabStats || (this.isCommentary && this.selectionNum === 0) || this.hideBall
    }
  },
  watch: {
    eventId(val, oldVal) {
      if (val && oldVal) {
        this.updateEventId(this.eventId)
        this.reset()
        this.currentTab = this.tab
        setParamSportId(this.$route.params.sportName)
      }

      useDetailStore().clearComboMarket()
      useDetailStore().getConflictMarkets([], true)
      useDetailStore().updateSub(true)
    },
    'event.status': {
      immediate: true,
      handler() {
        this.checkStatus()
      }
    },
    'event.sportId': {
      handler(newV) {
        this.isVSoccer = newV === 'lv:sport:1'
      },
      immediate: true
    },
    miniFooterVisible() {
      eventBus.$emit('mini-footer-visible')
    }
  },
  created() {
    if (this.tab && !/assist/i.test(this.tab)) {
      this.currentTab = this.tab
    }
    if (this.eventId) {
      this.updateEventId(this.eventId)
      this.reset()
      setParamSportId(this.$route.params.sportName)
    }
  },
  mounted() {
    useDomThemeSwitcher().syncTheme()

    eventBus.$on('update-mini-footer', (visible = true) => {
      this.checkMiniFooterVisible(visible)
    })

    eventBus.$on('commentary-betslip-ball', (visible) => {
      const betslipBall = document.querySelector('.m-betslip-ball')
      if (visible) {
        if (betslipBall) {
          this.hideBall = true
        }
      } else {
        if (betslipBall) {
          this.hideBall = false
        }
      }
    })

    this.$watch(
      () => this.isCommentary,
      (val) => {
        const elBetslip = document.querySelector('.m-betslip')
        if (elBetslip) {
          if (val) {
            elBetslip.classList.add('commentary-betslip')
          } else {
            elBetslip.classList.remove('commentary-betslip')
          }
        }
        eventBus.$emit('update-defaultVisibleIndex', val ? -1 : 0)
      }
    )
    this.$watch(
      () => this.shouldHideBetslipBall,
      (val) => {
        const elBetslip = document.querySelector('.m-betslip')
        if (elBetslip) {
          if (val) {
            elBetslip.classList.add('hide-ball')
          } else {
            elBetslip.classList.remove('hide-ball')
          }
        }
      },
      { immediate: true }
    )
  },
  beforeDestroy() {
    const elBetslip = document.querySelector('.m-betslip')
    if (elBetslip) {
      elBetslip.classList.remove('commentary-betslip')
      elBetslip.classList.remove('hide-ball')
    }
    this.subMarkets({ isSub: false })
    this.subEvent({ isSub: false })
    this.$dialog.close()
    this.resetEvent()
    resetMatchInfo()
    eventBus.$off('update-mini-footer')
    useMatchDetailStore().$reset()

    useDetailStore().clearComboMarket()
    useDetailStore().getConflictMarkets([], true)
    useDetailStore().updateSub(true)
  },
  methods: {
    checkStatus() {
      this.$dialog.close()

      const { status } = this.event
      if (status === 3 || status === 4) {
        // 比赛真正结束
        this.$dialog({
          message: 'Live game finished!',
          confirmButtonText: 'Result',
          cancelButtonText: 'More games',
          distinguishCancelAndClose: true,
          classNames: ['match-liveFinished-dialog']
        })
          .then(() => {
            // Result，点击页面跳转至比赛结果页面
            window.mstrace('LiveMatchDetail_Result', 'LiveMatchDetail')
            this.$router.replace({ name: 'matchResult', params: { eventId: this.event.eventId } })
            this.$dialog.close()
          })
          .catch((action) => {
            if (action === 'cancel') {
              window.mstrace('LiveMatchDetail_MoreGame', 'LiveMatchDetail')
              this.$router.push({ name: 'liveMatchList', params: { sportName: encodeURIComponent(this.event.sport) } })
              this.$dialog.close()
            } else if (action === 'close') {
              this.$router.replace({ name: 'matchResult', params: { eventId: this.event.eventId } })
              this.$dialog.close()
            }
          })
      }
    },
    onChangeNav(nav) {
      this.currentTab = nav
      const isMarket = /market/i.test(nav)
      this.checkMiniFooterVisible(isMarket)
    },
    reset() {
      this.resetEvent()
      this.resetLiveCommentary()
    },
    checkMiniFooterVisible(visible) {
      if (!visible) {
        this.miniFooterVisible = false
        return
      }
      this.$nextTick(() => {
        const { offsetHeight } = this.$refs.mainDetail.$el
        if (offsetHeight + 44 <= window.innerHeight) {
          this.miniFooterVisible = false
        } else {
          this.miniFooterVisible = true
        }
      })
    }
  }
}
</script>

<style lang="less">
.m-betslip.commentary-betslip {
  .m-betslip-ball {
    bottom: 80px;
  }
}

.m-betslip.hide-ball {
  .m-betslip-ball {
    display: none;
  }
}

.m-layout.page-matchDetail .m-header::after {
  height: 88px;
}

.match-liveFinished-dialog {
  .dialog--body .dialog-content {
    color: #333;
    font-size:20px;
    font-weight:600;
    text-align: center;
  }
}
</style>
